/**
 * ResizeObserver-Loop-Fehler-Handhabung (Bug: "Reklamation delegieren" blockiert)
 * ───────────────────────────────────────────────────────────────────────────────
 * Browser werfen "ResizeObserver loop completed with undelivered notifications"
 * (bzw. "ResizeObserver loop limit exceeded") als BENIGNES, nicht-fatales
 * Fehler-Event. Radix-UI-Komponenten (Select/Popover/Tooltip/ScrollArea) und
 * recharts-`ResponsiveContainer` nutzen `ResizeObserver` intensiv und loesen
 * diesen Loop regelmaessig aus — z. B. beim Oeffnen des Werk-Dropdowns im
 * Delegations-Modal (34 Eintraege + parallel resizende Charts).
 *
 * Im CRA-Dev-/Preview-Build faengt `react-error-overlay` dieses window-`error`-
 * Event ab und blendet ein bildschirmfuellendes rotes "Uncaught runtime errors"-
 * Overlay ein, das die UI (das Delegations-Modal) blockiert. In der W3C-Spec ist
 * der Loop-Hinweis rein informativ und KEIN echter Laufzeitfehler.
 *
 * Zwei verhaltensneutrale Massnahmen (kein anderer Fehler wird unterdrueckt):
 *   1) ResizeObserver-Callbacks in `requestAnimationFrame` verschieben →
 *      bricht den synchronen Mess-/Layout-Loop, der den Fehler ueberhaupt erzeugt.
 *   2) Das spezifische benigne Fehler-/Rejection-Event in der Capture-Phase
 *      abfangen, damit das Dev-Overlay es nicht anzeigt.
 */

const RESIZE_OBSERVER_MSG =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

// 1) ResizeObserver-Callback entkoppeln (rAF-Debounce) — verhindert den Loop-Fehler.
if (typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined') {
  const NativeResizeObserver = window.ResizeObserver;

  class PatchedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          // Beobachter koennte zwischenzeitlich getrennt sein → defensiv.
          if (Array.isArray(entries)) {
            callback(entries, observer);
          }
        });
      });
    }
  }

  window.ResizeObserver = PatchedResizeObserver;
}

// 2) Benignes Loop-Fehler-Event vor dem Dev-Overlay abfangen (Capture-Phase = laeuft zuerst).
if (typeof window !== 'undefined') {
  window.addEventListener(
    'error',
    (event) => {
      if (event && typeof event.message === 'string' && RESIZE_OBSERVER_MSG.test(event.message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true,
  );

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason;
    const msg = typeof reason === 'string' ? reason : reason && reason.message;
    if (typeof msg === 'string' && RESIZE_OBSERVER_MSG.test(msg)) {
      event.preventDefault();
    }
  });
}
